import { CheckCircleOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";

export const IMPORT_SUCCESS_TITLE = "Schedule successfully imported";

export const IMPORT_SUCCESS_MESSAGE =
  "Review the impact of schedule change and its effect on procurement timelines before you make this version active.";

export const VIEW_CHANGE_IMPACT_LABEL = "View Change Impact";
export const SKIP_FOR_NOW_LABEL = "Skip For Now";

export type OpcImportSuccessModalProps = {
  open: boolean;
  // Dismissal, however it is reached — the button, the X, the mask or Esc. All
  // four mean the same thing here, so they share one handler rather than letting
  // "closed the dialog" and "chose to skip" drift apart.
  onSkip: () => void;
  onViewChangeImpact: () => void;
};

/**
 * Reports a finished OPC import, and offers the review step that follows it.
 *
 * A dialog rather than the toast this replaces because it asks a question: an
 * imported schedule is not active yet, and reviewing its impact before
 * activating is the path the file-upload import already walks a user down
 * (import-schedule-tasks' post-upload panel, same copy). A toast cannot carry
 * that, and self-dismisses before it could be acted on.
 *
 * Deliberately presentational: the page owns which import finished and where
 * "View Change Impact" goes.
 */
function OpcImportSuccessModal({
  open,
  onSkip,
  onViewChangeImpact
}: Readonly<OpcImportSuccessModalProps>) {
  return (
    <Modal
      open={open}
      onCancel={onSkip}
      centered
      width={480}
      destroyOnHidden
      title={
        <div className="flex items-center gap-x-3">
          <CheckCircleOutlined
            className="text-xl text-ciq-success-text"
            data-testid="opc-import-success-icon"
          />
          <span className="text-base font-semibold text-black">
            {IMPORT_SUCCESS_TITLE}
          </span>
        </div>
      }
      footer={[
        <Button
          key="skip"
          type="text"
          onClick={onSkip}
          data-testid="opc-import-success-skip"
        >
          {SKIP_FOR_NOW_LABEL}
        </Button>,
        <Button
          key="view"
          type="primary"
          onClick={onViewChangeImpact}
          data-testid="opc-import-success-view"
        >
          {VIEW_CHANGE_IMPACT_LABEL}
        </Button>
      ]}
      data-testid="opc-import-success-modal"
    >
      {/* Indented past the icon so the sentence hangs off the title rather than
          the tick, which is what the design shows. */}
      <p
        className="mb-0 pl-8 text-sm text-black text-opacity-80"
        data-testid="opc-import-success-message"
      >
        {IMPORT_SUCCESS_MESSAGE}
      </p>
    </Modal>
  );
}

export default OpcImportSuccessModal;
